import logoImage from "@/assets/logo3.webp";
import React, { useMemo } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";

const DEFAULT_TITLE =
  "Best Digital Agency in Vancouver | Square Trigger";

const DEFAULT_DESCRIPTION =
  "A remote-first Digital Agency serving across Canada, Square Trigger delivers practical digital solutions & hands-on support for growing businesses. Get a quote!";

const CANONICAL_ORIGIN =
  "https://squaretrigger.com";

const BREADCRUMB_NAMES_BY_PATH = {
  "/": "Home Page",
  "/services": "Services Page",
  "/services/website-development": "Website Development",
  "/services/custom-tool-creation": "Custom Web Application",
  "/services/search-engine-optimization-company": "Search Engine Optimization",
  "/services/social-media-marketing-agency": "Social Media Marketing",
  "/services/website-maintenance-support": "Website Maintenance",
  "/services/rdsp-tool-for-financial-advisors": "RDSP tool for financial advisors",
  "/services/custom-dashboard-development": "Custom dashboard development",
  "/about": "About us",
  "/contact": "Contact",
  "/resources-faqs": "FAQs",
  "/blog": "Blog",
  "/work": "Square Trigger work",
  "/get-started": "Square Trigger get started",
  "/privacy-policy": "Square Trigger privacy policy",
  "/terms-of-service": "Square Trigger terms of services",
};

function getBreadcrumbSchema(pathname, pageTitle) {
  const paths = ["/"];

  if (pathname !== "/") {
    if (pathname.startsWith("/services/")) {
      paths.push("/services");
    } else if (pathname.startsWith("/blog/")) {
      paths.push("/blog");
    }

    paths.push(pathname);
  }

  const fallbackName = pageTitle
    .split("|")[0]
    .trim();

  return {
    "@context": "https://schema.org/",
    "@type": "BreadcrumbList",
    itemListElement: paths.map((path, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name:
        BREADCRUMB_NAMES_BY_PATH[path] ||
        fallbackName,
      item:
        path === "/"
          ? `${CANONICAL_ORIGIN}/`
          : `${CANONICAL_ORIGIN}${path}/`,
    })),
  };
}

function getAbsoluteImageUrl(imageUrl) {
  if (
    typeof imageUrl !== "string" ||
    !imageUrl.trim()
  ) {
    return `${CANONICAL_ORIGIN}/logo3.webp`;
  }

  const normalizedImageUrl =
    imageUrl.trim();

  if (
    normalizedImageUrl.startsWith(
      "http://"
    ) ||
    normalizedImageUrl.startsWith(
      "https://"
    )
  ) {
    return normalizedImageUrl;
  }

  return `${CANONICAL_ORIGIN}${
    normalizedImageUrl.startsWith("/")
      ? normalizedImageUrl
      : `/${normalizedImageUrl}`
  }`;
}

const DEFAULT_OG_IMAGE =
  getAbsoluteImageUrl(logoImage);

const DEFAULT_OG_IMAGE_ALT =
  "Square Trigger logo";

const OG_IMAGE_ALT_BY_PATH = {
  "/":
    "Square Trigger Digital Agency Vancouver",

  "/services":
    "Square Trigger Digital Services",

  "/services/website-development":
    "Website Development Services in Vancouver",

  "/services/custom-tool-creation":
    "Custom Web Application Development in Vancouver",

  "/services/search-engine-optimization-company":
    "Search Engine Optimization Services in Vancouver",

  "/services/social-media-marketing-agency":
    "Social Media Marketing Services in Vancouver",

  "/services/website-maintenance-support":
    "Website Maintenance Support in Vancouver",

  "/services/rdsp-tool-for-financial-advisors":
    "RDSP Tool for Financial Advisors",

  "/services/custom-dashboard-development":
    "Custom Dashboard Development",

  "/about":
    "About Square Trigger Vancouver",

  "/contact":
    "Contact Square Trigger Vancouver",

  "/resources-faqs":
    "Square Trigger Digital Agency FAQs",

  "/blog":
    "Square Trigger Digital Agency Blog",

  "/work":
    "Square Trigger Digital Solutions Case Studies",

  "/get-started":
    "Get Started with Square Trigger",

  "/digital-marketing-agency-surrey":
    "Digital Marketing Agency in Surrey BC",

  "/digital-marketing-agency-burnaby":
    "Digital Marketing Agency in Burnaby BC",

  "/digital-marketing-agency-richmond":
    "Digital Marketing Agency in Richmond BC",

  "/top-digital-agency-in-north-vancouver":
    "Digital Marketing Agency in North Vancouver",

  "/digital-marketing-agency-coquitlam":
    "Digital Marketing Agency in Coquitlam BC",

  "/digital-marketing-agency-langley":
    "Digital Marketing Agency in Langley BC",

  "/company/process":
    "Square Trigger Digital Project Process",

  "/company/values":
    "Square Trigger Values and Mission",

  "/company/careers":
    "Careers at Square Trigger",

  "/portfolio/case-studies":
    "Square Trigger Digital Project Case Studies",

  "/portfolio/gallery":
    "Square Trigger Digital Project Gallery",

  "/portfolio/testimonials":
    "Square Trigger Client Testimonials",

  "/resources/free":
    "Square Trigger Free Digital Resources",

  "/resources/news":
    "Square Trigger News and Press",
};

function normalizePathname(
  pathname = "/"
) {
  const normalizedPath =
    pathname === "/"
      ? "/"
      : `/${pathname.replace(
          /^\/+|\/+$/g,
          ""
        )}`;

  const canonicalPathMap = {
    "/company/about":
      "/about",

    "/faqs":
      "/resources-faqs",

    "/resources/faqs":
      "/resources-faqs",

    "/services/website-development-services":
      "/services/website-development",

    "/services/custom-application-development":
      "/services/custom-tool-creation",

    "/services/search-engine-optimization-services":
      "/services/search-engine-optimization-company",

    "/services/seo-services":
      "/services/search-engine-optimization-company",

    "/services/social-media-management-services":
      "/services/social-media-marketing-agency",

    "/services/social-media-management":
      "/services/social-media-marketing-agency",

    "/services/social-media-marketing":
      "/services/social-media-marketing-agency",

    "/services/website-maintenance-company":
      "/services/website-maintenance-support",

    "/services/maintenance-support":
      "/services/website-maintenance-support",

    "/rdsp-tool-for-financial-advisors":
      "/services/rdsp-tool-for-financial-advisors",

    "/custom-dashboard-development-vancouver":
      "/services/custom-dashboard-development",

    "/blogs":
      "/blog",

    "/digital-agency-surrey":
      "/digital-marketing-agency-surrey",

    "/digital-agency-burnaby":
      "/digital-marketing-agency-burnaby",

    "/digital-agency-richmond":
      "/digital-marketing-agency-richmond",

    "/digital-agency-north-vancouver":
      "/top-digital-agency-in-north-vancouver",

    "/digital-agency-coquitlam":
      "/digital-marketing-agency-coquitlam",

    "/digital-agency-langley":
      "/digital-marketing-agency-langley",
  };

  if (
    normalizedPath.startsWith(
      "/blogs/"
    )
  ) {
    return normalizedPath.replace(
      /^\/blogs\//,
      "/blog/"
    );
  }

  return (
    canonicalPathMap[normalizedPath] ||
    normalizedPath
  );
}

function normalizeKeywords(keywords) {
  if (Array.isArray(keywords)) {
    return keywords
      .filter(
        (keyword) =>
          typeof keyword === "string" &&
          keyword.trim()
      )
      .map((keyword) =>
        keyword.trim()
      )
      .join(", ");
  }

  if (typeof keywords === "string") {
    return keywords.trim();
  }

  return "";
}

function getSafeText(
  value,
  fallback
) {
  return (
    typeof value === "string" &&
    value.trim()
      ? value.trim()
      : fallback
  );
}

const SEO = ({
  title = DEFAULT_TITLE,
  description = DEFAULT_DESCRIPTION,
  keywords,
  ogTitle,
  ogDescription,
  ogSiteName = "Square Trigger",
  ogImage = DEFAULT_OG_IMAGE,
  ogImageAlt,
  ogType = "website",
  ogUrl,
  noIndex = false,
  structuredData,
}) => {
  const location = useLocation();

  const normalizedPath = useMemo(
    () =>
      normalizePathname(
        location.pathname
      ),
    [location.pathname]
  );

  const canonicalUrl = useMemo(
    () =>
      normalizedPath === "/"
        ? `${CANONICAL_ORIGIN}/`
        : `${CANONICAL_ORIGIN}${normalizedPath}/`,
    [normalizedPath]
  );

  const safeTitle = getSafeText(
    title,
    DEFAULT_TITLE
  );

  const safeDescription = getSafeText(
    description,
    DEFAULT_DESCRIPTION
  );

  const safeKeywords =
    normalizeKeywords(keywords);

  const safeOgTitle = getSafeText(
    ogTitle,
    safeTitle
  );

  const safeOgDescription =
    getSafeText(
      ogDescription,
      safeDescription
    );

  const safeOgSiteName = getSafeText(
    ogSiteName,
    "Square Trigger"
  );

  const safeOgType = getSafeText(
    ogType,
    "website"
  );

  const safeOgImage =
    getAbsoluteImageUrl(ogImage);

  const safeOgUrl = getSafeText(
    ogUrl,
    canonicalUrl
  );

  const safeOgImageAlt = getSafeText(
    ogImageAlt ||
      OG_IMAGE_ALT_BY_PATH[
        normalizedPath
      ],
    safeOgImage === DEFAULT_OG_IMAGE
      ? DEFAULT_OG_IMAGE_ALT
      : safeOgTitle
  );

  const websiteSchema = {
    "@context":
      "https://schema.org",
    "@type":
      "WebSite",
    name:
      "Square Trigger",
    url:
      `${CANONICAL_ORIGIN}/`,
    potentialAction: {
      "@type":
        "SearchAction",
      target:
        `${CANONICAL_ORIGIN}/blog/?s={search_term_string}`,
      "query-input":
        "required name=search_term_string",
    },
  };

  const organizationSchema = {
    "@context":
      "https://schema.org",
    "@type": "Organization",
    "@id": `${CANONICAL_ORIGIN}/#organization`,
    name:
      normalizedPath === "/contact"
        ? "Square Trigger Contact Page"
        : "Square Trigger",
    url: canonicalUrl,
    logo:
      `${CANONICAL_ORIGIN}/logo.webp`,
    sameAs: [
      "https://www.facebook.com/people/Square-Tigger/61585572042270/",
      "https://www.instagram.com/square_trigger.official/",
      "https://www.linkedin.com/company/square-trigger/",
    ],
  };

  const breadcrumbSchema =
    getBreadcrumbSchema(
      normalizedPath,
      safeTitle
    );

  return (
    <Helmet>
      <title>{safeTitle}</title>

      <meta
        name="description"
        content={safeDescription}
      />

      {safeKeywords ? (
        <meta
          name="keywords"
          content={safeKeywords}
        />
      ) : null}

      <meta
        name="robots"
        content={
          noIndex
            ? "noindex, nofollow"
            : "index, follow"
        }
      />

      <link
        rel="canonical"
        href={canonicalUrl}
      />

      <meta
        property="og:title"
        content={safeOgTitle}
      />

      <meta
        property="og:description"
        content={safeOgDescription}
      />

      <meta
        property="og:type"
        content={safeOgType}
      />

      <meta
        property="og:site_name"
        content={safeOgSiteName}
      />

      <meta
        property="og:locale"
        content="en_CA"
      />

      <meta
        property="og:url"
        content={safeOgUrl}
      />

      <meta
        property="og:image"
        content={safeOgImage}
      />

      <meta
        property="og:image:secure_url"
        content={safeOgImage}
      />

      <meta
        property="og:image:alt"
        content={safeOgImageAlt}
      />

      <meta
        name="twitter:card"
        content="summary_large_image"
      />

      <meta
        name="twitter:title"
        content={safeOgTitle}
      />

      <meta
        name="twitter:description"
        content={safeOgDescription}
      />

      <meta
        name="twitter:image"
        content={safeOgImage}
      />

      <meta
        name="twitter:image:alt"
        content={safeOgImageAlt}
      />

      {normalizedPath === "/" ? (
        <script type="application/ld+json">
          {JSON.stringify(
            websiteSchema
          )}
        </script>
      ) : null}

      {normalizedPath === "/" || normalizedPath === "/contact" ? (
        <script type="application/ld+json">
          {JSON.stringify(
            organizationSchema
          )}
        </script>
      ) : null}

      <script type="application/ld+json">
        {JSON.stringify(
          breadcrumbSchema
        )}
      </script>

      {structuredData
        ? (Array.isArray(structuredData)
            ? structuredData
            : [structuredData]
          ).map((schema, index) => (
            <script
              key={`structured-data-${index}`}
              type="application/ld+json"
            >
              {JSON.stringify(schema)}
            </script>
          ))
        : null}
    </Helmet>
  );
};

export default SEO;
